import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* env */
import * as env from "../../constants/env"

/* Components */
import { Input } from "../../components"
import { pushToast } from "../../components/Toast"
import { pushConfirm } from "../../components/Confirm"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"
import { TableIcon, NetIcon, CameraIcon, KeyIcon, InfoIcon, CopyIcon, TrashIcon, WarningIcon, DealerIcon, LayersIcon, EditIcon } from "../../components/Icons"

/* Widgets */
import Layout from "./Layout"
import GamePicker from "./GamePicker"

/* Icons Of The Types Of The Catalogue */
import { TypeIcon } from "../Game/GameIcons"

/* Helpers */
import { utils, withRouter } from "../../helpers"

/* REST API */
import { getSettings as get, saveSettings as save, removeSettings as remove } from "../../api/Table"

/* Copy To Clipboard */
import { CopyToClipboard } from "react-copy-to-clipboard"


/* THE TOKENS OF A TABLE — EVERY SERVICE READS ITS OWN ONE */
const TOKENS = [
    { key: "uid", label: "UNIQUE TOKEN" },
    { key: "dealerSocketToken", label: "DEALER MIDDLEWARE TOKEN" },
    { key: "dealerMonitorToken", label: "DEALER MONITOR TOKEN" },
    { key: "scannerToken", label: "SCANNER TOKEN" },
    { key: "adminToken", label: "ADMIN TOKEN" },
    { key: "atemToken", label: "ATEM TOKEN" },
]


/* Widget Settings */
class Settings extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: null,
            waiting: false,

            name: "",
            slug: "",
            game: "",
            gameURL: "",
            backURL: "",
            backColor: "",
            room: "",
            type: "",
            rtp: "",

            gameImgUrl: "",
            gameImg: null,
        }
    }

    componentDidMount = () => {
        this.load()
    }

    /* Load Table Data */
    load = (reload = false) => {

        /* Fields */
        const { uid } = this.props

        /* Check Params */
        if (!uid) {
            this.setState({ status: "error" })
            return
        }

        /* Refresh the page if necessary */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* START REST API */
        get({ uid }).then((response) => {
            if (response.status === 200) {

                /* Fields */
                const data = response.data

                this.setState({
                    status: "ok",
                    data,
                    name: utils.checkValue(data.name),
                    slug: utils.checkValue(data.slug),
                    game: utils.checkValue(data.game),
                    gameURL: utils.checkValue(data.gameURL),
                    backURL: utils.checkValue(data.backURL),
                    backColor: utils.checkValue(data.backColor),
                    room: utils.checkValue(data.room),
                    type: utils.checkValue(data.type),
                    rtp: utils.checkValue(data.rtp),
                    gameImg: null,
                    gameImgUrl: data.gameImg ? `${env.point}/images/table-images/${data.gameImg}` : "",
                })

            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission" })
            }
            else if (response.status === 498) {
                this.setState({ status: "key" })
            }
            else {
                this.setState({ status: "error" })
            }
        }).catch(() => {
            this.setState({ status: "network" })
        })
    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        return t("swal.generic")
    }

    /* THE GAME OF THE CATALOGUE BEHIND THE KEY — Tables.game KEEPS THE VERY SAME KEY */
    getGame = () => {

        /* Fields */
        const { catalog } = this.props
        const { game } = this.state

        /* Check Key */
        if (`${game}`.trim() === "") {
            return null
        }

        /* Send Result */
        return catalog.games.find((item) => item.key === game) || null
    }

    /* THE TYPE OF THE GAME OF THE TABLE */
    getType = () => {

        /* Fields */
        const { catalog } = this.props

        /* The Game Of The Catalogue */
        const found = this.getGame()

        /* Check Game */
        if (!found) {
            return null
        }

        /* Send Result */
        return catalog.types.find((item) => parseInt(item.id) === parseInt(found.typeID)) || null
    }

    /* THE STUDIO OF THE TABLE */
    getStudio = () => {

        /* Fields */
        const { catalog } = this.props
        const { data } = this.state

        /* Check Studio */
        if (!data || !data.studioID) {
            return null
        }

        /* Send Result */
        return catalog.studios.find((item) => parseInt(item.id) === parseInt(data.studioID)) || null
    }

    /* Remove Table */
    remove = () => {

        /* Fields */
        const { uid, navigate } = this.props
        const { name } = this.state

        /* Check Params */
        if (!uid) {
            pushToast({ text: t("widget.unknownError"), type: "error" })
            return
        }

        /* Ask */
        pushConfirm({
            title: t("swal.deleteGame"),
            text: `${name} — ${t("swal.deleteGameWarning")}`,
            icon: "warning",
            danger: true,
            confirmText: t("swal.deleteButton"),
            cancelText: t("swal.cancelButton"),
        }).then((confirmed) => {

            /* Check Answer */
            if (!confirmed) {
                return
            }

            /* START REST API */
            remove({ uid }).then((response) => {
                if (response.status === 200) {
                    pushToast({ text: t("swal.gameDeleted"), type: "success" })
                    navigate("/game")
                }
                else {
                    pushToast({ text: this.message(response), type: "error" })
                }
            }).catch(() => {
                pushToast({ text: t("swal.generic"), type: "error" })
            })

        })
    }

    /* Save Action */
    save = () => {

        /* Fields */
        const { uid } = this.props
        const { name, slug, game, gameURL, backURL, backColor, gameImg, room, type, rtp } = this.state

        /* Check Params */
        if (!uid) {
            pushToast({ text: t("widget.unknownError"), type: "error" })
            return
        }

        /* Check Name */
        if (name.trim() === "") {
            this._name.error(t("settings.enterName"))
            return
        }

        /* Check Key */
        if (slug.trim() === "") {
            this._slug.error(t("settings.enterKey"))
            return
        }

        /* Check Game */
        if (game.trim() === "") {
            pushToast({ text: t("settings.enterGame"), type: "error" })
            return
        }

        /* Prepare Request */
        const data = new FormData()

        data.append("uid", uid)
        data.append("name", name.trim())
        data.append("slug", slug.trim())
        data.append("game", game.trim())
        data.append("gameURL", gameURL.trim())
        data.append("backURL", backURL.trim())
        data.append("backColor", backColor.trim())
        data.append("room", room.trim())
        data.append("type", type.trim())
        data.append("rtp", rtp.trim())

        /* Image */
        if (gameImg) {
            data.append("gameImg", gameImg)
        }

        /* Start Waiting */
        this.setState({ waiting: true })

        /* START REST API */
        save(data).then((response) => {
            if (response.status === 200) {
                pushToast({ text: t("swal.settingsSaved"), type: "success", timer: 3000 })
                this.load()
            }
            else {
                pushToast({ text: this.message(response), type: "error" })
            }
        }).catch(() => {
            pushToast({ text: t("swal.generic"), type: "error" })
        }).finally(() => {
            this.setState({ waiting: false })
        })

    }

    /* Add Game Image */
    addGameImg = (event) => {

        /* Fields */
        const img = event.target.files[0]

        /* Check Image */
        if (!img) {
            return
        }

        this.setState({ gameImg: img, gameImgUrl: URL.createObjectURL(img) })
    }


    /* Draw One Read Only Row With A Copy Button */
    _copy = (label, value) => {

        /* Fields */
        const text = `${utils.checkValue(value)}`.trim()

        return (
            <div className="tbl--token" key={`${label}`}>

                <div className="tbl--token-text">
                    <span>{label}</span>
                    <p>{text === "" ? "—" : text}</p>
                </div>

                {text !== "" &&
                    <CopyToClipboard onCopy={() => pushToast({ text: t("settings.tokenCopied"), type: "success" })} text={text}>
                        <div className="tbl--token-copy" title={t("settings.copy")}>
                            <CopyIcon size={14} />
                        </div>
                    </CopyToClipboard>
                }

            </div>
        )
    }

    /* Draw One Read Only Row */
    _info = (icon, label, value) => (
        <div className="tbl--row">

            <div className="tbl--row-icon">{icon}</div>

            <div className="tbl--row-text">
                <h3>{`${utils.checkValue(value)}`.trim() === "" ? "—" : value}</h3>
                <p>{label}</p>
            </div>

        </div>
    )

    /* Draw The Game Of The Table — IT IS PICKED FROM THE CATALOGUE, THE KEY IS NEVER TYPED */
    _game = () => {

        /* Fields */
        const { game } = this.state
        const found = this.getGame()
        const type = this.getType()

        /* Fields — an unknown key breaks the route of the table, so it is drawn as an error */
        const empty = `${game}`.trim() === ""
        const unknown = !empty && !found

        return (
            <div className="tbl--game">

                <div className="tbl--row">

                    <div className={`tbl--row-icon ${unknown ? "red" : ""}`}>
                        {found ? <TypeIcon type={type ? type.key : ""} size={17} /> : <TableIcon size={16} />}
                    </div>

                    <div className="tbl--row-text">
                        <h3>{found ? found.name : (empty ? "—" : game)}</h3>
                        <p>{empty ? t("settings.gameNotSet") : (found ? (found.prefix ? `${found.prefix} · ${game}` : game) : t("settings.unknownGame"))}</p>
                    </div>

                    <div className="tbl--row-action">
                        <div onClick={() => this._picker.open(game)} className="tbl--small-button">
                            {empty ? t("common.select") : t("settings.changeGame")}
                        </div>
                    </div>

                </div>

            </div>
        )
    }

    /* THE VALUE THE NATIVE PICKER UNDERSTANDS — IT ONLY SPEAKS #rrggbb */
    getHex = () => {

        /* Fields */
        const value = `${this.state.backColor}`.trim()

        /* Short Hex — the picker refuses #abc */
        if (/^#[0-9a-f]{3}$/i.test(value)) {
            return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`
        }

        /* Full Hex */
        if (/^#[0-9a-f]{6}$/i.test(value)) {
            return value
        }

        /* Anything Else — a name, an rgba() or a gradient stays in the field, the picker starts from black */
        return "#000000"
    }

    /* Draw The Background Color — THE SWATCH ITSELF OPENS THE PICKER OF THE SYSTEM */
    _color = () => {

        /* Fields */
        const { backColor } = this.state

        /* Fields — an empty color keeps the checkerboard of the stylesheet */
        const empty = `${backColor}`.trim() === ""

        return (
            <div className="tbl--color">

                <div className="tbl--color-input">
                    <Input label={t("settings.backColor")} value={backColor} onChange={(event) => this.setState({ backColor: event.target.value })} />
                </div>

                <label className="tbl--swatch" style={empty ? undefined : { background: backColor }} title={t("settings.pickColor")}>

                    <div className="tbl--swatch-mark"><EditIcon size={10} /></div>

                    <input type="color" value={this.getHex()} onChange={(event) => this.setState({ backColor: event.target.value })} />

                </label>

            </div>
        )
    }

    /* Draw The Image Of The Table */
    _image = () => {

        /* Fields */
        const { gameImgUrl } = this.state

        return (
            <div className="tbl--image">

                {gameImgUrl !== ""
                    ? <img src={gameImgUrl} alt="Game" />
                    : <div className="tbl--image-empty"><CameraIcon size={22} /></div>
                }

                <label className="tbl--image-pick">
                    <input type="file" accept="image/*" onChange={(event) => this.addGameImg(event)} />
                    <span>{gameImgUrl !== "" ? t("settings.replaceImage") : t("settings.addImage")}</span>
                </label>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status, data, waiting, name, slug, gameURL, backURL, room, type, rtp } = this.state

        /* Layout */
        if (status !== "ok") {
            return <Layout status={status} reload={() => this.load(true)} />
        }

        /* Fields */
        const studio = this.getStudio()

        return (
            <div className="tbl--panel">

                {/* The Table Itself */}
                <div className="tbl--section">

                    <div className="tbl--section-title">
                        <TableIcon size={14} />
                        {t("settings.mainSection")}
                    </div>

                    <div className="tbl--inputs">
                        <Input ref={(ref) => this._name = ref} label={t("settings.tableName")} value={name} onChange={(event) => this.setState({ name: event.target.value })} />
                        <Input ref={(ref) => this._slug = ref} label={t("settings.tableKey")} value={slug} onChange={(event) => this.setState({ slug: event.target.value })} />
                    </div>

                    {/* Draw Game */}
                    {this._game()}

                    <div className="tbl--inputs">
                        <Input label={t("settings.type")} value={type} onChange={(event) => this.setState({ type: event.target.value })} />
                        <Input label={t("settings.room")} value={room} onChange={(event) => this.setState({ room: event.target.value })} />
                    </div>

                    <div className="tbl--inputs full">
                        <Input type="number" label="RTP" value={rtp} onChange={(event) => this.setState({ rtp: event.target.value })} />
                    </div>

                </div>


                {/* Links Of The Table */}
                <div className="tbl--section">

                    <div className="tbl--section-title">
                        <NetIcon size={14} />
                        {t("settings.linksSection")}
                    </div>

                    <div className="tbl--inputs full">
                        <Input label={t("settings.gameURL")} value={gameURL} onChange={(event) => this.setState({ gameURL: event.target.value })} />
                    </div>

                    <div className="tbl--inputs full">
                        <Input label={t("settings.serverURL")} value={backURL} onChange={(event) => this.setState({ backURL: event.target.value })} />
                    </div>

                    {/* The Service Of The Studio — it is set on the studio, not on the table */}
                    {this._copy(t("settings.serviceURL"), data.serviceURL)}

                </div>


                {/* Look Of The Table */}
                <div className="tbl--section">

                    <div className="tbl--section-title">
                        <CameraIcon size={14} />
                        {t("settings.designSection")}
                    </div>

                    {/* Draw Color */}
                    {this._color()}

                    {/* Draw Image */}
                    {this._image()}

                </div>


                {/* Tokens Of The Table */}
                <div className="tbl--section">

                    <div className="tbl--section-title">
                        <KeyIcon size={14} />
                        {t("settings.tokensSection")}
                    </div>

                    {TOKENS.map((token) => this._copy(token.label, data[token.key]))}

                </div>


                {/* Everything The Table Only Reports */}
                <div className="tbl--section">

                    <div className="tbl--section-title">
                        <InfoIcon size={14} />
                        {t("settings.infoSection")}
                    </div>

                    {this._info(<DealerIcon size={16} />, t("settings.currentDealer"), data.dealer)}
                    {this._info(<LayersIcon size={16} />, t("settings.studio"), studio ? studio.name : null)}

                    <div className="tbl--hint">{t("settings.parametersHint")}</div>

                </div>


                {/* The Table Is Removed Together With Everything It Holds */}
                <div className="tbl--section danger">

                    <div className="tbl--section-title">
                        <WarningIcon size={14} />
                        {t("settings.dangerSection")}
                    </div>

                    <div className="tbl--hint">{t("settings.deleteHint")}</div>

                    <div onClick={() => this.remove()} className="tbl--button danger">
                        <TrashIcon size={15} />
                        {t("swal.deleteGame")}
                    </div>

                </div>


                <div className="tbl--footer">

                    {waiting
                        ? <div className="tbl--button primary disabled"><LoadingOutlined /></div>
                        : <div onClick={() => this.save()} className="tbl--button primary">{t("common.save")}</div>
                    }

                </div>

                {/* The catalogue of the games — it opens above the panel */}
                <GamePicker ref={(ref) => this._picker = ref} select={(key) => this.setState({ game: key })} />

            </div>
        )
    }

}


const mapStateToProps = (state) => ({
    catalog: state.catalog,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Settings))
